import React, { useRef, useEffect, useId } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../i18n/useLanguage';

export type DateTimeClearButton = 'none' | 'text' | 'icon';

interface DateTimePickerProps {
  value?: string; // ISO 8601 string
  onChange: (value?: string) => void;
  error?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Clear control: hidden (Steckbrief form), text label, or × icon (e.g. search filters). */
  clearButton?: DateTimeClearButton;
}

/**
 * Component for selecting a date and time.
 * When a date is selected, the time is automatically set to the current time.
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  clearButton = 'text',
  ...props
}) => {
  const { t } = useLanguage();
  const inputId = useId();
  const label = props.label ?? t('dateTime.label');
  const placeholder = props.placeholder ?? t('dateTime.placeholder');
  const previousDatePortionRef = useRef<string | null>(null);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const clear = () => {
    previousDatePortionRef.current = null;
    onChange(undefined);
  };

  // Sync the ref with the current value prop's date portion
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      previousDatePortionRef.current = `${year}-${month}-${day}`;
    } else {
      previousDatePortionRef.current = null;
    }
  }, [value]);

  /** WebKit (iOS Safari) often omits `change` when the native value is cleared; `input` still fires. */
  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      clear();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!newValue) {
      clear();
      return;
    }

    const selectedDate = new Date(newValue);

    const currentDatePortion = newValue.split('T')[0];
    const previousDatePortion = previousDatePortionRef.current;

    if (currentDatePortion !== previousDatePortion) {
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      selectedDate.setHours(currentHours, currentMinutes, 0, 0);

      previousDatePortionRef.current = currentDatePortion;
    }

    onChange(selectedDate.toISOString());
  };

  /**
   * iOS Safari's native datetime sheet can clear or commit via its own controls
   * without firing `change`/`input`. Sync from the element value after close (rAF).
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    window.requestAnimationFrame(() => {
      const domValue = el.value;
      if (!domValue) {
        if (valueRef.current !== undefined) clear();
        return;
      }
      const iso = new Date(domValue).toISOString();
      if (iso !== valueRef.current) {
        previousDatePortionRef.current = domValue.split('T')[0];
        onChange(iso);
      }
    });
  };

  const formatForInput = (isoString?: string): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="form-group">
      <label htmlFor={inputId}>{label}</label>
      <div className="datetime-picker-field">
        <input
          type="datetime-local"
          id={inputId}
          value={formatForInput(value)}
          onChange={handleChange}
          onInput={handleInput}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`input${error ? ' input--error' : ''}`}
          disabled={disabled}
        />
        {clearButton === 'icon' ? (
          <button
            type="button"
            className={clsx(
              'datetime-picker-clear',
              'datetime-picker-clear--icon',
              (!value || disabled) && 'datetime-picker-clear--reserved'
            )}
            onClick={clear}
            tabIndex={value && !disabled ? 0 : -1}
            aria-hidden={!value || disabled}
            aria-label={t('post.clear')}
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden />
          </button>
        ) : value && !disabled ? (
          <button
            type="button"
            className="datetime-picker-clear"
            onClick={clear}
            aria-label={t('post.clear')}
          >
            {t('post.clear')}
          </button>
        ) : null}
      </div>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};
